import { Controller } from "@hotwired/stimulus";
import { updatePreviews } from "./dropzone/preview_helper";

export default class extends Controller {
  static targets = ["fileInput", "previewContainer", "button", "icon", "label"];
  static values = { url: String, urlMethod: String, previewFlag: { type: Boolean, default: true } };

  connect() {
    this.files = [];
    this.isMultipleUploads = this.fileInputTarget.multiple;
    this.element.addEventListener("dragover", this.preventDragDefaults);
    this.element.addEventListener("dragenter", this.preventDragDefaults);

    // Repopulate the file input field with uploaded files
    const uploadedFilesData = this.fileInputTarget.dataset.uploadedFiles;
    if (uploadedFilesData) {
      try {
        const uploadedFiles = JSON.parse(uploadedFilesData);
        this.addUploadedFiles(uploadedFiles);
      } catch (error) {
        console.error("Failed to parse uploaded files:", error);
      }
    }
  }

  disconnect() {
    this.element.removeEventListener("dragover", this.preventDragDefaults);
    this.element.removeEventListener("dragenter", this.preventDragDefaults);
  }

  preventDragDefaults(e) {
    e.preventDefault();
    e.stopPropagation();
  }

  trigger() {
    this.fileInputTarget.click();

    // Synchronize the file input field after the dialog closes
    setTimeout(() => {
      this.resetInputField();
    }, 0);
  }

  acceptFiles(event) {
    event.preventDefault();

    const newFiles = event.dataTransfer ? event.dataTransfer.files : event.target.files;
    // Check if any files were selected
    if (newFiles && newFiles.length > 0) {
      const filesArray = Array.from(newFiles);
      this.addFiles(filesArray);
    } else {
      console.log("No files selected. Preserving existing files.");
      this.resetInputField(); // Ensure the file input field reflects the current state
    }
  }

  addUploadedFiles(uploadedFiles) {
    // Recreate File objects from Base64-encoded data
    const simulatedFiles = uploadedFiles.map((fileData) => {
      const parsedFile = JSON.parse(fileData);
      const metadata = parsedFile.metadata;
      const byteCharacters = atob(parsedFile.file_content);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: metadata.content_type });
      return new File([blob], metadata.filename, { type: metadata.content_type });
    });

    this.addFiles(simulatedFiles);
  }

  addFiles(newFiles) {
    const validFiles = newFiles.filter((file) => !this.isFileDuplicate(file));
    if (validFiles.length === 0) {
      console.log("No valid or unique files to add.");
      return;
    }

    if (this.isMultipleUploads) {
      this.files = [...this.files, ...validFiles];
    } else {
      this.files = [validFiles[0]];
    }

    if (this.previewFlagValue) {
      updatePreviews(this.previewContainerTarget, this.files, this.removeFile.bind(this));
    }

    this.resetInputField();
    this.uploadFiles();
  }

  isFileDuplicate(file) {
    return this.files.some((existingFile) => existingFile.name === file.name);
  }

  removeFile(fileToRemove) {
    this.files = this.files.filter((file) => file !== fileToRemove);

    if (this.previewFlagValue) {
      updatePreviews(this.previewContainerTarget, this.files, this.removeFile.bind(this));
    }

    this.resetInputField();
  }

  resetInputField() {
    const dataTransfer = new DataTransfer();
    this.files.forEach((file) => dataTransfer.items.add(file));
    this.fileInputTarget.files = dataTransfer.files;
  }

  uploadFiles() {
    if (this.urlValue && this.urlMethodValue) {
      if (this.files.length > 0) {
        const formData = new FormData();
        this.files.forEach((file) => {
          formData.append('attachments[]', file);
        });

        $.ajax({
          url: this.urlValue,
          method: this.urlMethodValue,
          data: formData,
          processData: false,
          contentType: false,
          dataType: "script",
          headers: { "X-CSRF-Token": $.rails.csrfToken() },
          beforeSend: () => this.setUploading(true),
          success: () => {
            this.files = [];
            if (this.previewFlagValue) {
              updatePreviews(this.previewContainerTarget, this.files, this.removeFile.bind(this));
            }
            this.resetInputField();
          },
          error: (status, err) => {
            console.error("Upload failed:", status, err);
          },
          complete: () => this.setUploading(false),
        });
      } else {
        console.warn("No files to upload.");
      }
    } else {
      console.warn("Upload URL and method not defined. Skipping upload.");
    }
  }

  setUploading(state) {
    if (!this.previewFlagValue) {
      if (this.hasButtonTarget) {
        this.buttonTarget.toggleAttribute("disabled", state);
        this.buttonTarget.setAttribute("aria-busy", state ? "true" : "false");
      }

      if (this.hasIconTarget) {
        this.iconTarget.innerHTML = state ? `<path d="M12 19V6" /><path d="M5 12l7-7 7 7" /><path d="M5 19h14" />` : `<path d="M15.5 2H8.6c-.4 0-.8.2-1.1.5-.3.3-.5.7-.5 1.1v12.8c0 .4.2.8.5 1.1.3.3.7.5 1.1.5h9.8c.4 0 .8-.2 1.1-.5.3-.3.5-.7.5-1.1V6.5L15.5 2z" /><path d="M3 7.6v12.8c0 .4.2.8.5 1.1.3.3.7.5 1.1.5h9.8" /><path d="M15 2v5h5" />`;
      }

      if (this.hasLabelTarget) {
        this.labelTarget.textContent = state ? "Uploading …" : "Drag Files to Upload or Click Here";
      }
    }
  }
}
